import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { exchangeSessionId } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // ── Branded Google OAuth (Jun 16, 2026) ──────────────────────
      // Google redirects here with `?code=...&state=...` (query string,
      // NOT hash). We just forward to the backend callback which does
      // the token exchange + cookie set, then bounces us to /dashboard.
      const qs = new URLSearchParams(window.location.search);
      const code = qs.get('code');
      const state = qs.get('state');
      const googleError = qs.get('error');
      if (googleError) {
        navigate(`/login?google_error=${encodeURIComponent(googleError)}`, { replace: true });
        return;
      }
      if (code && state) {
        // Hard-redirect so the browser follows the Set-Cookie header
        // on the backend response. (A fetch() would NOT persist the
        // session cookie because we'd never navigate.)
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        window.location.href = `${API_URL}/api/auth/google/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
        return;
      }

      // ── Legacy Emergent-managed flow (hash-based session_id) ─────
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (sessionId) {
        const userData = await exchangeSessionId(sessionId);
        window.history.replaceState(null, '', window.location.pathname);
        if (userData) {
          navigate('/dashboard', { replace: true, state: { user: userData } });
        } else {
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [exchangeSessionId, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
